/* Admin → Hierarchy page.
 *
 * Renders the Zone → City → RSM → ASM → Distributor → Retailer → Outlet
 * hierarchy loaded from Demo_User_Hierarchy_Access_Data.xlsx. Provides:
 * - Table view of all 16+ rows with search
 * - Reseed action (wipes + reloads from packaged JSON) that also creates
 *   demo login users (Retailer, Distributor, ASM, RSM), one per unique
 *   name, with the correct scope pre-mapped from the hierarchy.
 */
import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { http, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Network, RefreshCw, RotateCcw, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ADMIN_ROLES = new Set(["admin", "spykar_admin", "system_admin"]);

const COLS = [
    ["outlet_code", "Outlet Code"],
    ["zone", "Zone"],
    ["city", "City"],
    ["rsm", "RSM"],
    ["asm", "ASM"],
    ["distributor", "Distributor"],
    ["retailer", "Retailer"],
    ["outlet", "Outlet"],
];

export default function AdminHierarchy() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");
    const [reseeding, setReseeding] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await http.get("/admin/hierarchy");
            setRows(r.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { if (user && ADMIN_ROLES.has(user.role)) load(); }, [user]);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return rows;
        return rows.filter((r) => COLS.some(([k]) => String(r[k] || "").toLowerCase().includes(s)));
    }, [rows, q]);

    // Aggregate for the summary cards
    const stats = useMemo(() => {
        const dim = (k) => new Set(rows.map((r) => r[k])).size;
        return {
            zones: dim("zone"),
            rsm: dim("rsm"),
            asm: dim("asm"),
            distributors: dim("distributor"),
            retailers: dim("retailer"),
            outlets: rows.length,
        };
    }, [rows]);

    if (user && !ADMIN_ROLES.has(user.role)) {
        return <Navigate to="/" replace />;
    }

    const reseed = async () => {
        if (!window.confirm("Wipe hierarchy and reseed from Demo_User_Hierarchy_Access_Data.xlsx? This also creates demo login users (password: Demo@2026).")) return;
        setReseeding(true);
        try {
            const r = await http.post("/admin/hierarchy/reseed");
            toast.success(`Hierarchy reseeded — ${r.data.hierarchy_rows} rows · ${r.data.users_created} demo users created`);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setReseeding(false);
        }
    };


    return (
        <div className="space-y-4" data-testid="admin-hierarchy-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="flex items-center gap-2 text-[20px] font-semibold">
                        <Network className="h-5 w-5" /> Sales Hierarchy
                    </h1>
                    <p className="text-[12px] text-muted-foreground">
                        Zone → City → RSM → ASM → Distributor → Retailer → Outlet. Powers scope filtering on DSR FORM.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={load}
                        data-testid="admin-hierarchy-refresh"
                        className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-background px-3 py-1.5 text-[12px] hover:bg-muted/40"
                    >
                        <RefreshCw className="h-3.5 w-3.5" /> Refresh
                    </button>
                    <button
                        onClick={reseed}
                        disabled={reseeding}
                        data-testid="admin-hierarchy-reseed"
                        className="inline-flex items-center gap-1.5 rounded-sm bg-amber-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                    >
                        <RotateCcw className="h-3.5 w-3.5" /> {reseeding ? "Reseeding…" : "Reseed + Create Demo Users"}
                    </button>
                </div>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
                {[
                    ["Zones", stats.zones],
                    ["RSMs", stats.rsm],
                    ["ASMs", stats.asm],
                    ["Distributors", stats.distributors],
                    ["Retailers", stats.retailers],
                    ["Outlets", stats.outlets],
                ].map(([label, val]) => (
                    <div key={label} className="rounded-sm border border-border bg-card p-2" data-testid={`hierarchy-stat-${label.toLowerCase()}`}>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
                        <div className="mono-num mt-0.5 text-lg font-semibold text-teal-700">{val}</div>
                    </div>
                ))}
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                    type="text"
                    placeholder="Search by outlet, city, RSM, distributor…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    data-testid="admin-hierarchy-search"
                    className="w-full rounded-sm border border-border bg-background py-1.5 pl-7 pr-2 text-[12px] focus:border-primary/60 focus:outline-none"
                />
            </div>

            {/* Table */}
            <div className="overflow-hidden rounded-sm border border-border bg-card">
                <div className="border-b border-border bg-muted/40 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {filtered.length} of {rows.length} row(s)
                </div>
                <div className="max-h-[560px] overflow-auto">
                    <table className="w-full text-[11px]">
                        <thead className="sticky top-0 z-10 bg-card text-[10px] uppercase tracking-wider text-muted-foreground shadow-sm">
                            <tr>
                                {COLS.map(([k, label]) => (
                                    <th key={k} className="whitespace-nowrap px-3 py-2 text-left">{label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => (
                                <tr key={r.outlet_code} className="border-t border-border hover:bg-muted/30" data-testid={`hierarchy-row-${r.outlet_code}`}>
                                    {COLS.map(([k]) => (
                                        <td key={k} className="whitespace-nowrap px-3 py-1.5">{r[k]}</td>
                                    ))}
                                </tr>
                            ))}
                            {!loading && filtered.length === 0 && (
                                <tr>
                                    <td colSpan={COLS.length} className="px-3 py-8 text-center text-muted-foreground">
                                        {rows.length === 0 ? "No hierarchy rows yet. Click Reseed to load." : "No rows match your search."}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Demo login helper */}
            <div className="rounded-sm border border-teal-600/30 bg-teal-50/40 p-3 text-[12px] dark:bg-teal-950/20" data-testid="hierarchy-demo-login-hint">
                <div className="mb-1 font-semibold">Demo logins (after reseed)</div>
                <div className="text-muted-foreground">
                    Every unique name in RSM / ASM / Distributor / Retailer gets a login. Email pattern:
                    <span className="mx-1 rounded-sm bg-muted px-1 py-0.5 font-mono text-[11px]">slug(name)@demo.fundle.ai</span>
                    (e.g. <code>rajesh-sharma@demo.fundle.ai</code>, <code>north-rsm-01@demo.fundle.ai</code>).
                    Password: <code className="rounded-sm bg-muted px-1 py-0.5 font-mono">Demo@2026</code>.
                    Each user's scope is pre-mapped to the outlets they are responsible for.
                </div>
            </div>
        </div>
    );
}
